import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  SalesComparisonApproachType,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectCompSequence,
  selectCompTypes,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectSubjectUnitRateForFeature } from '../propertyDataSelectors/unitRateSelectors.ts';
import { selectSubjectDepreciationRatioForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { calculateUnitRateAdjustment } from './utils/calculateUnitRateAdjustment.ts';

const calculateFinishedBasementAreaAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatio: number | undefined,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
  salesComparisonApproach: SalesComparisonApproachType,
  unitRate?: AloftPrice,
) => {
  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;

  if (compType === 'arv') {
    subjectImprovements = improvements.subjectArv;
  }
  const compImprovements = improvements[compID];

  const subjectPropertyIdentification = propertyIdentification.subject;

  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(depreciationRatio) ||
    isUndefined(subjectPropertyIdentification)
  ) {
    return undefined;
  }

  const {
    basement,
    craftsmanQuality: quality,
    propertyType = 'singleFamilyResidence',
  } = subjectImprovements;
  const { basement: compBasement } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;

  const finishedArea = isDefined(basement?.finishedArea)
    ? AloftArea.schema.parse(basement?.finishedArea)
    : AloftArea.zero;

  const compFinishedArea = isDefined(compBasement?.finishedArea)
    ? AloftArea.schema.parse(compBasement?.finishedArea)
    : AloftArea.zero;

  if (salesComparisonApproach === 'unit_rate') {
    return calculateUnitRateAdjustment({
      unitRate,
      subjectUnitAmount: finishedArea.toSqFt().unitAmount,
      compUnitAmount: compFinishedArea.toSqFt().unitAmount,
    });
  }

  if (isUndefined(quality) || (quality * 10) % 1 !== 0 || quality < 1 || isUndefined(zipCode)) {
    return undefined;
  }

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  return adjCalculator.getFinishedBasementAreaAdj({
    subject: {
      finishedArea: AloftArea.schema.parse(finishedArea),
      quality,
      propertyType,
      zipCode,
    },
    comp: { finishedArea: AloftArea.schema.parse(compFinishedArea) },
    depreciationRatio,
  }).adjustment;
};

export const selectFinishedBasementAreaAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    (state, id: string) => id,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'basementFinished'),
  ],
  calculateFinishedBasementAreaAdj,
);

export const selectFinishedBasementAreaAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectCompSequence,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'basementFinished'),
  ],
  (
    adjustmentVersion,
    improvements,
    propertyIdentification,
    depreciationRatio,
    compSequence,
    compTypes,
    salesComparisonApproach,
    unitRate,
  ) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateFinishedBasementAreaAdj(
        adjustmentVersion,
        improvements,
        propertyIdentification,
        depreciationRatio,
        propID,
        compTypes,
        salesComparisonApproach,
        unitRate,
      );
      if (isDefined(adj)) acc[propID] = adj;
      return acc;
    }, {}),
);
